
import React, { useState, useEffect } from 'react';
import { Site, Asset, AssetType, AssetStatus } from '../../types';
import { Server, Search, Plus, Filter, MoreVertical, Edit2, Trash2, CheckCircle2, AlertTriangle, XCircle, HardDrive, Wifi, Router, Monitor } from 'lucide-react';
import { Button } from '../shared';

// If supabase client is not available in props/context, we might need to pass data or use the hook.
// For now, I'll assume usage of the same pattern as other components or directly use the client if available.
// To keep it clean, I will ask to pass "assets" as prop or fetch them. 
// Given the architecture, I'll implement internal fetching/state management for now to be self-contained 
// OR rely on props if the parent manages state. 
// Let's implement fully functional component with internal fetch for simplicity unless passed.

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://bibwlxyarvyydqutmtih.supabase.co';
const supabaseKey = 'sb_publishable_aWtRhEztigQFWjoURPbzwg_ypPOvpfD'; // Using the key found in other files
const supabaseClient = createClient(supabaseUrl, supabaseKey); // Local client for this component

interface AssetsInventoryProps {
    sites: Site[];
}

export const AssetsInventory: React.FC<AssetsInventoryProps> = ({ sites }) => {
    const [assets, setAssets] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterSiteId, setFilterSiteId] = useState<string>('ALL');
    const [filterType, setFilterType] = useState<AssetType | 'ALL'>('ALL');

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [editingAsset, setEditingAsset] = useState<Partial<Asset> | null>(null);

    useEffect(() => {
        fetchAssets();
    }, []);

    const fetchAssets = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabaseClient.from('assets').select('*');
            if (error) throw error;

            // Map DB snake_case to TS camelCase
            const mappedAssets: Asset[] = (data || []).map((a: any) => ({
                id: a.id,
                name: a.name,
                type: a.type,
                brand: a.brand,
                model: a.model,
                serialNumber: a.serial_number,
                ipAddress: a.ip_address,
                macAddress: a.mac_address,
                location: a.location,
                status: a.status,
                siteId: a.site_id,
                notes: a.notes,
                updatedAt: new Date(a.updated_at)
            }));
            setAssets(mappedAssets);
        } catch (err) {
            console.error('Error fetching assets:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!editingAsset || !editingAsset.name || !editingAsset.siteId || !editingAsset.type) {
            alert('Por favor complete los campos obligatorios (Nombre, Sitio, Tipo)');
            return;
        }

        try {
            const payload = {
                name: editingAsset.name,
                type: editingAsset.type,
                brand: editingAsset.brand,
                model: editingAsset.model,
                serial_number: editingAsset.serialNumber,
                ip_address: editingAsset.ipAddress,
                mac_address: editingAsset.macAddress,
                location: editingAsset.location,
                status: editingAsset.status || 'ACTIVE',
                site_id: editingAsset.siteId,
                notes: editingAsset.notes,
                updated_at: new Date().toISOString()
            };

            if (editingAsset.id) {
                // Update
                const { error } = await supabaseClient.from('assets').update(payload).eq('id', editingAsset.id);
                if (error) throw error;
            } else {
                // Insert
                const { error } = await supabaseClient.from('assets').insert(payload);
                if (error) throw error;
            }

            setShowModal(false);
            setEditingAsset(null);
            fetchAssets(); // Refresh
        } catch (err: any) {
            alert('Error al guardar: ' + err.message);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('¿Está seguro de eliminar este activo?')) return;
        try {
            const { error } = await supabaseClient.from('assets').delete().eq('id', id);
            if (error) throw error;
            setAssets(assets.filter(a => a.id !== id));
        } catch (err) {
            console.error('Error deleting:', err);
        }
    };

    const getIconForType = (type: AssetType) => {
        switch (type) {
            case 'ROUTER': return <Router size={18} className="text-blue-600" />;
            case 'SWITCH': return <Server size={18} className="text-indigo-600" />;
            case 'AP': return <Wifi size={18} className="text-green-600" />;
            case 'UPS': return <HardDrive size={18} className="text-orange-600" />;
            case 'POS': return <Monitor size={18} className="text-purple-600" />;
            default: return <Server size={18} className="text-gray-600" />;
        }
    };

    const getStatusBadge = (status: AssetStatus) => {
        switch (status) {
            case 'ACTIVE': return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center w-fit"><CheckCircle2 size={12} className="mr-1" /> Activo</span>;
            case 'MAINTENANCE': return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold flex items-center w-fit"><AlertTriangle size={12} className="mr-1" /> Mantenimiento</span>;
            case 'BROKEN': return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold flex items-center w-fit"><XCircle size={12} className="mr-1" /> Dañado</span>;
            default: return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-bold w-fit">{status}</span>;
        }
    };

    const filteredAssets = assets.filter(a => {
        const matchesSearch = a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            a.ipAddress?.includes(searchQuery) ||
            a.serialNumber?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesSite = filterSiteId === 'ALL' || a.siteId === filterSiteId;
        const matchesType = filterType === 'ALL' || a.type === filterType;
        return matchesSearch && matchesSite && matchesType;
    });

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Inventario de Activos</h1>
                    <p className="text-gray-500 text-sm">Gestión de equipamiento de red e infraestructura por tienda.</p>
                </div>
                <div className="flex gap-3">
                    <Button
                        onClick={() => {
                            setEditingAsset({ status: 'ACTIVE', type: 'ROUTER' });
                            setShowModal(true);
                        }}
                        variant="primary"
                    >
                        <Plus size={18} className="mr-2" /> Agregar Activo
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Buscar por nombre, IP, serie..."
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-black"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="flex gap-2">
                    <select
                        className="px-4 py-2 border rounded-lg bg-white"
                        value={filterSiteId}
                        onChange={e => setFilterSiteId(e.target.value)}
                    >
                        <option value="ALL">Todas las Tiendas</option>
                        {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <select
                        className="px-4 py-2 border rounded-lg bg-white"
                        value={filterType}
                        onChange={e => setFilterType(e.target.value as any)}
                    >
                        <option value="ALL">Todos los Tipos</option>
                        <option value="ROUTER">Router</option>
                        <option value="SWITCH">Switch</option>
                        <option value="AP">Access Point</option>
                        <option value="UPS">UPS</option>
                        <option value="POS">POS</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-xs">
                            <tr>
                                <th className="px-6 py-3">Dispositivo</th>
                                <th className="px-6 py-3">Tienda / Ubicación</th>
                                <th className="px-6 py-3">Detalles Técnicos</th>
                                <th className="px-6 py-3">Estado</th>
                                <th className="px-6 py-3 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan={5} className="p-8 text-center text-gray-500">Cargando inventario...</td></tr>
                            ) : filteredAssets.length === 0 ? (
                                <tr><td colSpan={5} className="p-8 text-center text-gray-500">No se encontraron activos.</td></tr>
                            ) : (
                                filteredAssets.map(asset => {
                                    const site = sites.find(s => s.id === asset.siteId);
                                    return (
                                        <tr key={asset.id} className="hover:bg-gray-50 transition">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div className="p-2 bg-gray-100 rounded-lg mr-3">
                                                        {getIconForType(asset.type)}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-gray-900">{asset.name}</div>
                                                        <div className="text-xs text-gray-500">{asset.brand} {asset.model}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-gray-900">{site?.name || 'Desconocido'}</div>
                                                <div className="text-xs text-gray-500">{asset.location || 'Sin ubicación específica'}</div>
                                            </td>
                                            <td className="px-6 py-4 font-mono text-xs">
                                                <div className="flex flex-col space-y-1">
                                                    {asset.ipAddress && <span className="flex items-center opacity-80"><Server size={10} className="mr-1" /> IP: {asset.ipAddress}</span>}
                                                    {asset.serialNumber && <span className="flex items-center opacity-60"><MoreVertical size={10} className="mr-1" /> S/N: {asset.serialNumber}</span>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {getStatusBadge(asset.status)}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button onClick={() => { setEditingAsset(asset); setShowModal(true); }} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit2 size={16} /></button>
                                                    <button onClick={() => handleDelete(asset.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {showModal && editingAsset && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden">
                        <div className="p-6 border-b bg-gray-50 flex justify-between items-center">
                            <h3 className="font-bold text-lg">{editingAsset.id ? 'Editar Activo' : 'Nuevo Activo'}</h3>
                            <button onClick={() => setShowModal(false)}><XCircle className="text-gray-400 hover:text-black" /></button>
                        </div>
                        <div className="p-6 grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block text-sm font-bold mb-1">Nombre del Dispositivo *</label>
                                <input className="w-full border p-2 rounded" value={editingAsset.name || ''} onChange={e => setEditingAsset({ ...editingAsset, name: e.target.value })} placeholder="Ej: Router Principal" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-1">Tienda *</label>
                                <select className="w-full border p-2 rounded bg-white" value={editingAsset.siteId || ''} onChange={e => setEditingAsset({ ...editingAsset, siteId: e.target.value })}>
                                    <option value="">Seleccionar Tienda...</option>
                                    {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-1">Tipo de Dispositivo *</label>
                                <select className="w-full border p-2 rounded bg-white" value={editingAsset.type || 'ROUTER'} onChange={e => setEditingAsset({ ...editingAsset, type: e.target.value as any })}>
                                    <option value="ROUTER">Router</option>
                                    <option value="SWITCH">Switch</option>
                                    <option value="AP">Access Point</option>
                                    <option value="UPS">UPS</option>
                                    <option value="POS">POS</option>
                                    <option value="OTHER">Otro</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Marca</label>
                                <input className="w-full border p-2 rounded" value={editingAsset.brand || ''} onChange={e => setEditingAsset({ ...editingAsset, brand: e.target.value })} placeholder="Cisco, Fortinet..." />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Modelo</label>
                                <input className="w-full border p-2 rounded" value={editingAsset.model || ''} onChange={e => setEditingAsset({ ...editingAsset, model: e.target.value })} />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Dirección IP</label>
                                <input className="w-full border p-2 rounded font-mono" value={editingAsset.ipAddress || ''} onChange={e => setEditingAsset({ ...editingAsset, ipAddress: e.target.value })} placeholder="192.168.1.1" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Número de Serie</label>
                                <input className="w-full border p-2 rounded font-mono" value={editingAsset.serialNumber || ''} onChange={e => setEditingAsset({ ...editingAsset, serialNumber: e.target.value })} />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Estado</label>
                                <select className="w-full border p-2 rounded bg-white" value={editingAsset.status || 'ACTIVE'} onChange={e => setEditingAsset({ ...editingAsset, status: e.target.value as any })}>
                                    <option value="ACTIVE">Activo</option>
                                    <option value="MAINTENANCE">En Mantenimiento</option>
                                    <option value="BROKEN">Dañado / Falla</option>
                                    <option value="RETIRED">Retirado</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Ubicación Local</label>
                                <input className="w-full border p-2 rounded" value={editingAsset.location || ''} onChange={e => setEditingAsset({ ...editingAsset, location: e.target.value })} placeholder="Ej: Rack Piso 2" />
                            </div>
                        </div>
                        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                            <Button onClick={() => setShowModal(false)} variant="secondary">Cancelar</Button>
                            <Button onClick={handleSave} variant="primary">Guardar Activo</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
